import { useMemo, useState, useEffect } from 'react';
import {
  Box,
  Button,
  IconButton,
  Popover,
  Stack,
  Typography,
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import dayjs from 'dayjs';
import { formatWeekRangeLabel, getWeekStartDate, WEEKDAY_LABELS } from '../../utils/week';

const generateCalendarDays = (reference) => {
  const startOfMonth = reference.startOf('month');
  const firstGridDay = startOfMonth.subtract((startOfMonth.day() + 6) % 7, 'day');
  return Array.from({ length: 42 }, (_, idx) => firstGridDay.add(idx, 'day'));
};

const WeekSelector = ({ value, onChange }) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const selectedStart = useMemo(() => getWeekStartDate(value), [value]);
  const [viewMonth, setViewMonth] = useState(selectedStart);

  useEffect(() => {
    setViewMonth(getWeekStartDate(value));
  }, [value]);

  const calendarDays = useMemo(() => generateCalendarDays(viewMonth), [viewMonth]);
  const selectedEnd = selectedStart.add(6, 'day');
  const weekLabel = formatWeekRangeLabel(value);

  const handleDayPick = (day) => {
    onChange?.(getWeekStartDate(day).format('YYYY-MM-DD'));
    setAnchorEl(null);
  };

  const isWithinSelectedWeek = (day) => day.isAfter(selectedStart.subtract(1, 'day')) && day.isBefore(selectedEnd.add(1, 'day'));

  const renderDayCell = (day) => {
    const isCurrentMonth = day.month() === viewMonth.month();
    const isMonday = day.day() === 1;
    const inSelectedWeek = isWithinSelectedWeek(day);
    const isToday = day.isSame(dayjs(), 'day');

    return (
      <Box
        key={day.toString()}
        onClick={() => handleDayPick(day)}
        sx={{
          width: 38,
          height: 38,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: 1.5,
          bgcolor: inSelectedWeek ? 'rgba(37,99,235,0.15)' : 'transparent',
          cursor: 'pointer',
          color: isCurrentMonth ? '#0f172a' : '#94A3B8',
          position: 'relative',
          transition: 'background 0.2s ease, transform 0.2s ease',
          '&:hover': {
            bgcolor: 'rgba(37,99,235,0.25)',
            transform: 'scale(1.02)',
          },
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '50%',
            border: isMonday ? '1px solid #2563EB' : '1px solid transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontWeight: inSelectedWeek ? 700 : isToday ? 600 : 500,
            color: inSelectedWeek ? '#1D4ED8' : isToday ? '#2563EB' : undefined,
          }}
        >
          {day.date()}
        </Box>
      </Box>
    );
  };

  return (
    <Box sx={{ minWidth: 260 }}>
      <Button
        variant="outlined"
        fullWidth
        startIcon={<CalendarMonthIcon />}
        onClick={(event) => setAnchorEl(event.currentTarget)}
        sx={{ textTransform: 'none', justifyContent: 'space-between', borderRadius: 2 }}
      >
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {weekLabel}
        </Typography>
      </Button>

      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        slotProps={{
          paper: {
            sx: {
              mt: 1,
              borderRadius: 3,
              overflow: 'hidden',
              boxShadow: '0px 12px 28px rgba(15,23,42,0.18)',
            },
          },
        }}
      >
        <Box sx={{ p: 2, width: 310 }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
            <IconButton
              size="small"
              onClick={() => setViewMonth((prev) => prev.subtract(1, 'month'))}
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="subtitle2" sx={{ textTransform: 'capitalize' }}>
              {viewMonth.format('MMMM YYYY')}
            </Typography>
            <IconButton size="small" onClick={() => setViewMonth((prev) => prev.add(1, 'month'))}>
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(7, 1fr)',
              gap: 0.5,
              textAlign: 'center',
              mb: 1,
            }}
          >
            {WEEKDAY_LABELS.map((label) => (
              <Typography key={label} variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                {label}
              </Typography>
            ))}
          </Box>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 0.5 }}>
            {calendarDays.map((day) => renderDayCell(day))}
          </Box>
        </Box>
      </Popover>
    </Box>
  );
};

export default WeekSelector;

